<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="700"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
            />
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                @cancel="overlay = false"
                :delete-action="deleteAction"
                @remove="remove"
            />
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12">
                                    <strong>{{ $t('common.name') }} *</strong>
                                    <w-text-field
                                        v-model="name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="!isSection && !isFolder && addAction">
                                    <strong>{{ $t('common.file') }} *</strong>
                                    <v-file-input
                                        v-model="data"
                                        outlined
                                        dense
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-paperclip"
                                        :color="inputColor"
                                        :background-color="backgroundColor"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                        hide-details="auto"
                                    ></v-file-input>
                                </v-col>
                                <v-col cols="12">
                                    <strong>Numéro</strong>
                                    <w-text-field v-model="number"/>
                                </v-col>
                                <v-col cols="12">
                                    <strong>TAGS</strong>
                                    <w-data-select-autocomplete
                                        v-model="tagIds"
                                        multiple
                                        type="EDMS_TAG"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <strong>{{ $t('common.description') }}</strong>
                                    <w-simple-text-area
                                        v-model="description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <v-row dense v-if="isSection || isFolder">
                            <v-col cols="12">
                                <v-checkbox
                                    v-model="inbox"
                                    label="Inbox"
                                    hide-details
                                    :readonly="readonly"
                                />
                            </v-col>
                        </v-row>
                        <w-fieldset v-if="inbox && (isSection || isFolder)">
                            <v-row dense>
                                <v-col cols="8">
                                    <strong>Email</strong>
                                    <w-text-field
                                        v-model="email"
                                        :readonly="readonly"
                                        :rules="[mxRules.required, mxRules.email]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <strong>Password</strong>
                                    <w-text-field
                                        v-model="password"
                                        :readonly="readonly"
                                        type="password"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="8">
                                    <strong>Host</strong>
                                    <w-text-field
                                        v-model="host"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <strong>Port</strong>
                                    <w-text-field
                                        v-model="port"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <v-btn
                    depressed
                    color="teal"
                    small
                    outlined
                    @click="close"
                >
                    {{ $t('common.close') }}
                    <v-icon right>mdi-close</v-icon>
                </v-btn>
                <v-btn
                    v-if="addAction"
                    depressed
                    color="teal"
                    class="white--text"
                    small
                    :loading="loading"
                    @click="save"
                >
                    {{ $t('common.add') }}
                    <v-icon right>mdi-plus-circle-outline</v-icon>
                </v-btn>
                <v-btn
                    v-if="updateAction"
                    depressed
                    color="orange"
                    class="white--text"
                    small
                    :loading="loading"
                    @click="update"
                >
                    {{ $t('common.edit') }}
                    <v-icon right>mdi-pencil-box-multiple</v-icon>
                </v-btn>
                <v-btn
                    v-if="deleteAction"
                    depressed
                    color="red"
                    class="white--text"
                    small
                    :loading="loading"
                    @click="overlay = true"
                >
                    {{ $t('common.delete') }}
                    <v-icon right>mdi-trash-can</v-icon>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import {useDocumentStore} from "@/stores/Edms/Document";
import {computed, onMounted, ref} from "vue";
import useTools from "@/use/Tools";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    props: {
        module: String,
        parentId: Number,
        dialog: Boolean,
        isSection: Boolean,
        isFolder: Boolean,
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props, {emit}) {
        const {mxRules} = useValidation();
        const {loading, overlay, inputColor, backgroundColor,} = useSharedData();
        const {transformFilterValues} = useSharedMethod();
        const {$refs, $route} = useTools();

        const readonly = computed(() => props.deleteAction);

        const documentStore = useDocumentStore();

        const data = ref(null);
        const id = ref(null);
        const name = ref(null);
        const number = ref(null);
        const tagIds = ref([]);
        const description = ref(null);
        const inbox = ref(false);
        const email = ref(null);
        const password = ref(null);
        const host = ref(null);
        const port = ref(null);

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                let formData = new FormData();
                formData.append('file', data.value);
                axios.post(`/documents`, formData, {
                    params: transformFilterValues({
                        module: props.module,
                        moduleId: $route.params.id,
                        parentId: props.parentId,
                        name: name.value,
                        number: number.value,
                        tagIds: tagIds.value,
                        description: description.value,
                        section: props.isSection,
                        folder: props.isFolder,
                        inbox: inbox.value,
                        email: email.value,
                        password: password.value,
                        host: host.value,
                        port: port.value
                    }),
                }).then(response => {
                    if (props.isSection) {
                        documentStore.addSection(response.data);
                    } else {
                        documentStore.addDocument(props.parentId, response.data);
                    }
                    close();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                let formData = new FormData();
                formData.append('file', data.value);
                axios.put(`/documents`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    params: transformFilterValues({
                        id: id.value,
                        name: name.value,
                        number: number.value,
                        tagIds: tagIds.value,
                        description: description.value,
                        inbox: inbox.value,
                        email: email.value,
                        password: password.value,
                        host: host.value,
                        port: port.value
                    }),
                }).then(response => {
                    documentStore.updateDocument(props.parentId, response.data);
                    close();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/documents/${id.value}`).then(() => {
                documentStore.deleteDocument(props.parentId, documentStore.fichier);
                close();
            }).finally(() => {
                loading.value = false;
            })
        }

        function close() {
            overlay.value = false;
            emit('close')
            $refs.form.reset();
        }

        function setProperties() {
            id.value = documentStore.fichier.id;
            name.value = documentStore.fichier.name;
            number.value = documentStore.fichier.number;
            tagIds.value = documentStore.fichier.tagIds;
            description.value = documentStore.fichier.description;
            inbox.value = documentStore.fichier.inbox;
            email.value = documentStore.fichier.email;
            password.value = documentStore.fichier.password;
            host.value = documentStore.fichier.host;
            port.value = documentStore.fichier.port;
        }

        onMounted(() => {
            if (!props.addAction)
                setProperties();
        });


        return {
            mxRules,
            loading,
            overlay,
            inputColor,
            backgroundColor,
            data,
            name,
            description,
            number,
            tagIds,
            inbox,
            email,
            password,
            host,
            port,
            readonly,
            close,
            save,
            remove,
            update,
            setProperties
        }
    }
}
</script>

<style scoped>

</style>